import React, {createContext, ReactNode, useContext, useEffect, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {PosOrderSummary} from "@/data/entities/order";
import {api} from "@/data/api";

export const POS_ORDERS_QUERY_KEY = "pos-orders";
export const POS_ORDERS_REFRESH_INTERVAL = 60_000;

const NO_ORDERS: PosOrderSummary[] = [];

type PosOrdersContextType = {
    orders: PosOrderSummary[],
    activeOrderCount: number,
    isLoading: boolean,
    isError: boolean,
    isRefreshing: boolean,
    lastUpdatedAt: number,
    refresh: () => void,
    selectedDay: string | null,
    setSelectedDay: (day: string | null) => void,
};

const PosOrdersContext = createContext<PosOrdersContextType | undefined>(undefined);

type Props = {
    children: ReactNode,
};

const PosProvider = ({children}: Props) => {
    const {dashboardLocation} = useRestaurant();
    const [selectedDay, setSelectedDay] = useState<string | null>(null);
    const {data, isPending, isError, isFetching, dataUpdatedAt, errorUpdatedAt, refetch} = useQuery({
        queryKey: [POS_ORDERS_QUERY_KEY, dashboardLocation.id, selectedDay ?? "today"],
        queryFn: () => api.orders.listOrders(dashboardLocation.id, selectedDay ?? undefined),
    });

    const lastUpdatedAt = Math.max(dataUpdatedAt, errorUpdatedAt);

    // Refetch one interval after the last (successful or failed) fetch.
    // Restarts after a manual refresh or invalidation.
    useEffect(() => {
        if (lastUpdatedAt === 0)
            return; // First fetch still running

        const timeout = window.setTimeout(
            () => refetch({cancelRefetch: false}),
            Math.max(0, lastUpdatedAt + POS_ORDERS_REFRESH_INTERVAL - Date.now()),
        );
        return () => window.clearTimeout(timeout);
    }, [lastUpdatedAt, refetch]);

    const orders = data ?? NO_ORDERS;
    const value = useMemo<PosOrdersContextType>(() => ({
        orders,
        activeOrderCount: orders.filter(o => o.cancelledOn === null).length,
        isLoading: isPending,
        isError,
        isRefreshing: isFetching,
        lastUpdatedAt,
        refresh: () => refetch({cancelRefetch: false}),
        selectedDay,
        setSelectedDay,
    }), [orders, isPending, isError, isFetching, lastUpdatedAt, refetch, selectedDay]);

    return (
        <PosOrdersContext.Provider value={value}>
            {children}
        </PosOrdersContext.Provider>
    );
};

export const usePos = (): PosOrdersContextType => {
    const context = useContext(PosOrdersContext);
    if (context === undefined)
        throw new Error("usePos must be used within a PosOrdersProvider");

    return context;
};

export default PosProvider;